import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, User, Car, Building, Percent, Power, PowerOff, Package, FolderOpen, ArrowLeftRight, Briefcase } from 'lucide-react';
import type { AnySystemParameter } from '../types/system-parameters.types';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  item: AnySystemParameter | null;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  item,
  onClose,
  onConfirm,
  loading = false
}) => {
  if (!item) return null;

  // Determinar si es un toggle (activar/desactivar) o eliminación física
  const isToggleCategory = ['movement_types', 'presentations', 'product_categories', 'positions', 'drivers', 'vehicles', 'transport', 'fiscal'].includes(item.category);

  // Para categorías de toggle, determinar si es activación o desactivación basado en el estado actual
  // Fiscal usa item.isActive directamente, otros usan additionalData.isActive
  const isCurrentlyActive = isToggleCategory && (item.category === 'fiscal' ? item.isActive : (item as any).additionalData?.isActive);
  const isReactivation = isToggleCategory && !isCurrentlyActive;
  const isDeactivation = isToggleCategory && isCurrentlyActive;

  const getIcon = () => {
    const toggleColor = isReactivation ? 'text-green-500' : 'text-orange-500';

    switch (item.category) {
      case 'fiscal':
        return <Percent className={`h-12 w-12 ${toggleColor} mx-auto`} />;
      case 'drivers':
        return <User className={`h-12 w-12 ${toggleColor} mx-auto`} />;
      case 'vehicles':
        return <Car className={`h-12 w-12 ${toggleColor} mx-auto`} />;
      case 'transport':
        return <Building className={`h-12 w-12 ${toggleColor} mx-auto`} />;
      case 'positions':
        return <Briefcase className={`h-12 w-12 ${toggleColor} mx-auto`} />;
      case 'movement_types':
        return <ArrowLeftRight className={`h-12 w-12 ${toggleColor} mx-auto`} />;
      case 'presentations':
        return <Package className={`h-12 w-12 ${toggleColor} mx-auto`} />;
      case 'product_categories':
        return <FolderOpen className={`h-12 w-12 ${toggleColor} mx-auto`} />;
      default:
        return <AlertTriangle className="h-12 w-12 text-red-500 mx-auto" />;
    }
  };

  const getItemDisplayName = () => {
    switch (item.category) {
      case 'fiscal':
        return item.name;
      case 'drivers':
        return (item as any).additionalData.fullName;
      case 'vehicles':
        return `${(item as any).additionalData.brand} ${(item as any).additionalData.model} - ${(item as any).additionalData.plate}`;
      case 'transport':
        return (item as any).additionalData.companyName;
      default:
        return item.name;
    }
  };

  const getCategoryLabel = () => {
    switch (item.category) {
      case 'fiscal':
        return 'parámetro fiscal';
      case 'drivers':
        return 'conductor';
      case 'vehicles':
        return 'vehículo';
      case 'transport':
        return 'empresa de transporte';
      case 'positions':
        return 'cargo';
      case 'movement_types':
        return 'tipo de movimiento';
      case 'presentations':
        return 'presentación';
      case 'product_categories':
        return 'categoría de producto';
      default:
        return 'elemento';
    }
  };

  const getWarningMessage = () => {
    switch (item.category) {
      case 'fiscal':
        return isReactivation
          ? 'Este parámetro fiscal volverá a estar activo y disponible para cálculos del sistema.'
          : 'Este parámetro fiscal quedará inactivo y no se aplicará en los cálculos del sistema. Podrás reactivarlo más adelante.';
      case 'drivers':
        return isReactivation
          ? 'Este conductor volverá a estar activo y disponible para asignar a vehículos y viajes.'
          : 'Este conductor quedará inactivo y no estará disponible para nuevas asignaciones. Los registros históricos no se verán afectados. Podrás reactivarlo más adelante.';
      case 'vehicles':
        return isReactivation
          ? 'Este vehículo volverá a estar activo y disponible para asignar a viajes.'
          : 'Este vehículo quedará inactivo y no estará disponible para nuevas asignaciones. Los registros históricos no se verán afectados. Podrás reactivarlo más adelante.';
      case 'transport':
        return isReactivation
          ? 'Esta empresa de transporte volverá a estar activa y disponible para operaciones.'
          : 'Esta empresa de transporte quedará inactiva y no estará disponible para nuevas operaciones. Los registros históricos no se verán afectados. Podrás reactivarla más adelante.';
      case 'positions':
        return isReactivation
          ? 'Este cargo volverá a estar activo y disponible para asignar a empleados.'
          : 'Este cargo quedará inactivo y no estará disponible para nuevas asignaciones. Los empleados actuales con este cargo no se verán afectados. Podrás reactivarlo más adelante.';
      case 'movement_types':
        return isReactivation
          ? 'Este tipo de movimiento volverá a estar activo y disponible para nuevos registros.'
          : 'Este tipo de movimiento quedará inactivo y no estará disponible para nuevos registros. Los registros existentes no se verán afectados. Podrás reactivarlo más adelante.';
      case 'presentations':
        return isReactivation
          ? 'Esta presentación volverá a estar activa y disponible para asignar a productos.'
          : 'Esta presentación quedará inactiva y no estará disponible para asignar a productos. Los productos existentes no se verán afectados. Podrás reactivarla más adelante.';
      case 'product_categories':
        return isReactivation
          ? 'Esta categoría volverá a estar activa y disponible para asignar a productos.'
          : 'Esta categoría quedará inactiva y no estará disponible para asignar a productos. Los productos existentes no se verán afectados. Podrás reactivarla más adelante.';
      default:
        return 'Esta acción no se puede deshacer.';
    }
  };

  // Determinar textos y colores según la acción
  const getActionText = () => {
    if (isReactivation) return 'reactivar';
    if (isDeactivation) return 'desactivar';
    return 'eliminar';
  };

  const getActionTextCapitalized = () => {
    if (isReactivation) return 'Reactivar';
    if (isDeactivation) return 'Desactivar';
    return 'Eliminar';
  };

  const getLoadingText = () => {
    if (isReactivation) return 'Reactivando...';
    if (isDeactivation) return 'Desactivando...';
    return 'Eliminando...';
  };

  const getTitleText = () => {
    if (isReactivation) return 'Confirmar Reactivación';
    if (isDeactivation) return 'Confirmar Desactivación';
    return 'Confirmar Eliminación';
  };

  const getColorClasses = () => {
    if (isReactivation) return {
      bg: 'bg-green-50 border-green-200',
      icon: 'text-green-600',
      title: 'text-green-800',
      text: 'text-green-700',
      button: 'bg-green-600 hover:bg-green-700'
    };
    if (isDeactivation) return {
      bg: 'bg-orange-50 border-orange-200',
      icon: 'text-orange-600',
      title: 'text-orange-800',
      text: 'text-orange-700',
      button: 'bg-orange-600 hover:bg-orange-700'
    };
    return {
      bg: 'bg-red-50 border-red-200',
      icon: 'text-red-600',
      title: 'text-red-800',
      text: 'text-red-700',
      button: 'bg-red-600 hover:bg-red-700'
    };
  };

  const colors = getColorClasses();
  const articleForCategory = ['presentación', 'categoría de producto'].includes(getCategoryLabel()) ? 'esta' : 'este';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            {getTitleText()}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-center">
          {getIcon()}

          <div className="space-y-2">
            <p className="text-lg font-medium">
              ¿Estás seguro de que deseas {getActionText()} {articleForCategory} {getCategoryLabel()}?
            </p>

            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">
                {getItemDisplayName()}
              </p>
              {item.description && (
                <p className="text-sm text-gray-600 mt-1">
                  {item.description}
                </p>
              )}
            </div>
          </div>

          <div className={`flex items-start gap-2 p-3 ${colors.bg} border rounded-lg text-left`}>
            {isReactivation ? (
              <Power className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
            ) : isDeactivation ? (
              <PowerOff className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
            ) : (
              <AlertTriangle className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
            )}
            <div>
              <p className={`font-semibold ${colors.title} text-sm`}>
                {isToggleCategory ? 'Información' : 'Advertencia'}
              </p>
              <p className={`text-sm ${colors.text} mt-1`}>
                {getWarningMessage()}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant={isToggleCategory ? 'default' : 'destructive'}
            onClick={onConfirm}
            disabled={loading}
            className={colors.button}
          >
            {loading ? getLoadingText() : getActionTextCapitalized()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};